import React, { useEffect, useMemo, useRef, useState } from "react";

import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import ButtonDropdown from "@cloudscape-design/components/button-dropdown";
import Checkbox from "@cloudscape-design/components/checkbox";
import Multiselect from "@cloudscape-design/components/multiselect";
import FormField from "@cloudscape-design/components/form-field";
import Header from "@cloudscape-design/components/header";
import Icon from "@cloudscape-design/components/icon";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Spinner from "@cloudscape-design/components/spinner";
import Tabs from "@cloudscape-design/components/tabs";
import Textarea from "@cloudscape-design/components/textarea";
import { colorBackgroundContainerHeader, colorBorderDividerDefault, borderRadiusContainer } from "@cloudscape-design/design-tokens";

import Poster from "../assets/poster.png";
import GamesSectionGameIdsInput from "./GamesSectionGameIdsInput";
import { isGameSectionActive } from "../helpers/gamesSectionAdmin";
import { parseGameIdList, applyBadgeToGames, clearBadgesForGames, parseSectionJson } from "../helpers/gamesSectionGamesEditor";

const gameIdsEqual = (left, right) => String(left) === String(right);

const gameCardOrdersEqual = (left, right) =>
	left.length === right.length && left.every((game, index) => gameIdsEqual(game.gameID, right[index].gameID));

const updateMovedGameIdsForAddedCard = (movedGameIds, gameId, baselineIndex, nextIndex) => {
	const nextMovedGameIds = movedGameIds.filter((movedGameId) => !gameIdsEqual(movedGameId, gameId));
	return baselineIndex >= 0 && baselineIndex !== nextIndex
		? [...nextMovedGameIds, String(gameId)]
		: nextMovedGameIds;
};

const restoreCardToInitialIndex = (cards, initialOrderedIds, gameId) => {
	const initialIndex = initialOrderedIds.findIndex((id) => gameIdsEqual(id, gameId));
	const currentIndex = cards.findIndex((game) => gameIdsEqual(game.gameID, gameId));
	if (initialIndex < 0 || currentIndex < 0) return cards;
	const nextCards = [...cards];
	const [movedGame] = nextCards.splice(currentIndex, 1);
	nextCards.splice(Math.min(initialIndex, nextCards.length), 0, movedGame);
	return nextCards;
};

const getErrorText = (error, fallback) => {
	if (typeof error === "string") return error;
	if (typeof error?.text === "string") return error.text;
	if (typeof error?.message === "string") return error.message;
	return fallback;
};

const isGamesOrderConflict = (error) => getErrorText(error, "").includes("GAME_SECTION_ORDER_CONFLICT");

const isInterfaceApiError = (error) =>
	Boolean(error) &&
	Object.prototype.hasOwnProperty.call(error, "text") &&
	Object.prototype.hasOwnProperty.call(error, "code");

const badgePositionStyles = {
	top_left: { top: 0, left: 0, borderRadius: "0 0 25px 0" },
	top_right: { top: 0, right: 0, borderRadius: "0 0 0 25px" },
	bottom_left: { bottom: 0, left: 0, borderRadius: "0 25px 0 0" },
	bottom_right: { bottom: 0, right: 0, borderRadius: "25px 0 0 0" },
};

const badgePositions = ["top_left", "top_right", "bottom_left", "bottom_right"];

const cloneAssignmentsByGameId = (assignmentsByGameId) =>
	Object.fromEntries(
		Object.entries(assignmentsByGameId).map(([gameId, assignments]) => [
			gameId,
			assignments.map((assignment) => ({ ...assignment })),
		]),
	);

const mergeDefinitions = (definitions, createdDefinitions) => {
	const definitionsById = new Map(definitions.map((definition) => [String(definition.id), definition]));
	createdDefinitions.forEach((definition) => definitionsById.set(String(definition.id), { ...definition }));
	return [...definitionsById.values()];
};

const replaceCommittedAssignments = (assignmentsByGameId, badgeChanges) => {
	const next = cloneAssignmentsByGameId(assignmentsByGameId);
	badgeChanges.forEach(({ gameId, assignments }) => {
		next[String(gameId)] = assignments.map((assignment) => ({ ...assignment }));
	});
	return next;
};

const getDefinition = (definitions, badgeId) =>
	definitions.find((definition) => String(definition.id) === String(badgeId));

const isRtpAssignment = (assignment, definitions) =>
	assignment?.systemType === "rtpUp" ||
	getDefinition(definitions, assignment?.badgeId)?.kind === "rtpUp";

const getAssignmentAtPosition = (assignmentsByGameId, gameId, position) =>
	(assignmentsByGameId[String(gameId)] || []).find((assignment) => assignment.position === position) || null;

const replaceAssignmentAtPosition = (assignmentsByGameId, gameId, position, nextAssignment) => {
	const gameIdKey = String(gameId);
	const assignments = (assignmentsByGameId[gameIdKey] || []).filter(
		(assignment) => assignment.position !== position,
	);
	return {
		...assignmentsByGameId,
		[gameIdKey]: nextAssignment ? [...assignments, nextAssignment] : assignments,
	};
};

const getRtpIdentityValue = (value) => {
	if (value === "" || value === null || typeof value === "undefined") return "";
	const numericValue = Number(value);
	return Number.isInteger(numericValue) && numericValue > 0 ? String(numericValue) : String(value);
};

const getSlotIdentity = (assignment, definitions) => {
	if (!assignment) return "empty";
	if (isRtpAssignment(assignment, definitions)) return `rtpUp:${getRtpIdentityValue(assignment.rtpValue)}`;
	return `custom:${String(assignment.badgeId)}`;
};

const getBadgeIdentity = (assignment, definitions) => {
	if (!assignment) return null;
	return isRtpAssignment(assignment, definitions) ? "rtpUp" : `custom:${String(assignment.badgeId)}`;
};

const isValidRtpValue = (value) => {
	if (value === "" || value === null || typeof value === "undefined") return false;
	const numericValue = Number(value);
	return Number.isInteger(numericValue) && numericValue > 0;
};

const getDirtyPositionsByGameId = (baselineAssignmentsByGameId, draftAssignmentsByGameId, definitions) => {
	const result = {};
	const gameIds = new Set([
		...Object.keys(baselineAssignmentsByGameId),
		...Object.keys(draftAssignmentsByGameId),
	]);
	gameIds.forEach((gameId) => {
		const dirtyPositions = badgePositions.filter((position) => {
			const baseline = getAssignmentAtPosition(baselineAssignmentsByGameId, gameId, position);
			const draft = getAssignmentAtPosition(draftAssignmentsByGameId, gameId, position);
			return getSlotIdentity(baseline, definitions) !== getSlotIdentity(draft, definitions);
		});
		if (dirtyPositions.length) result[gameId] = dirtyPositions;
	});
	return result;
};

const buildBadgeChanges = (dirtyGameIds, assignmentsByGameId, definitions) =>
	dirtyGameIds.map((gameId) => ({
		gameId: Number(gameId),
		assignments: badgePositions.flatMap((position) => {
			const assignment = getAssignmentAtPosition(assignmentsByGameId, gameId, position);
			if (!assignment) return [];
			if (isRtpAssignment(assignment, definitions)) {
				return [{
					systemType: "rtpUp",
					position,
					rtpValue: Number(assignment.rtpValue),
				}];
			}
			return [{ badgeId: assignment.badgeId, position }];
		}),
	}));

function GamesSectionGamesEditor({ Interface, gameStore, availableGameIds, badgeBootstrap, onBadgesCommitted, onClose, canImportSection }) {
	const [sections, setSections] = useState([]);
	const [sectionsLoading, setSectionsLoading] = useState(true);
	const [sectionsError, setSectionsError] = useState("");
	const [selectedSection, setSelectedSection] = useState(null);
	const [saveError, setSaveError] = useState("");
	const [saving, setSaving] = useState(false);
	const [initialOrderedIds, setInitialOrderedIds] = useState([]);
	const [currentCards, setCurrentCards] = useState([]);
	const [addSelectionTab, setAddSelectionTab] = useState("selected");
	const [idsCopyError, setIdsCopyError] = useState("");
	const [selectedCardIds, setSelectedCardIds] = useState([]);
	const [bulkIds, setBulkIds] = useState("");
	const [bulkResult, setBulkResult] = useState("");
	const [bulkBadge, setBulkBadge] = useState(null);
	const [bulkPosition, setBulkPosition] = useState(null);
	const [bulkRtp, setBulkRtp] = useState("");
	const [bulkBadgeError, setBulkBadgeError] = useState("");
	const [bulkBadgeOpen, setBulkBadgeOpen] = useState(false);
	const [toolView, setToolView] = useState(null);
	const [jsonTab, setJsonTab] = useState("export");
	const [jsonDraft, setJsonDraft] = useState("");
	const [jsonError, setJsonError] = useState("");
	const [generatedJson, setGeneratedJson] = useState(null);
	const [exportError, setExportError] = useState("");
	const [jsonImportPending, setJsonImportPending] = useState(false);
	const [activeVariant, setActiveVariant] = useState("before");
	const [inactiveDraft, setInactiveDraft] = useState(null);
	const [beforeImportIds, setBeforeImportIds] = useState(null);
	const importReviewActive = beforeImportIds !== null;
	const reviewVisible = importReviewActive && activeVariant === "imported";
	const [reviewDetailsOpen, setReviewDetailsOpen] = useState(false);
	const [reviewDetailsTab, setReviewDetailsTab] = useState("Added");
	const [activeMode, setActiveMode] = useState("available");
	const [draggedGameId, setDraggedGameId] = useState(null);
	const [movedGameIds, setMovedGameIds] = useState([]);
	const [badgeDefinitions, setBadgeDefinitions] = useState([]);
	const [baselineAssignmentsByGameId, setBaselineAssignmentsByGameId] = useState({});
	const [draftAssignmentsByGameId, setDraftAssignmentsByGameId] = useState({});
	const mountedRef = useRef(true);
	const draggedGameIdRef = useRef(null);
	const draggedModeRef = useRef(null);
	const dragSnapshotRef = useRef(null);
	const dragPreviewCardsRef = useRef(null);
	const lastPreviewTargetIdRef = useRef(null);
	const acceptedDropRef = useRef(false);
	const dragPreviewChangedRef = useRef(false);
	useEffect(() => {
		setBulkBadgeOpen(false);
	}, [activeMode, selectedSection?.id, toolView]);
	useEffect(() => {
		if (!selectedCardIds.length) setBulkBadgeOpen(false);
	}, [selectedCardIds]);
	const availabilitySet = useMemo(
		() => new Set(availableGameIds.map(String)),
		[availableGameIds],
	);
	const storeGamesById = useMemo(
		() => new Map(gameStore.data.map((game) => [String(game.gameID), game])),
		[gameStore.data],
	);
	const addGameCatalog = useMemo(
		() =>
			gameStore.data
				.filter((game) => availabilitySet.has(String(game.gameID)))
				.map((game) => ({
					...game,
					availableToClient: true,
					metadataStatus: "found",
				})),
		[availabilitySet, gameStore.data],
	);

	const buildSectionCards = (orderedGameIds) =>
		orderedGameIds.map((gameId) => {
			const storeGame = storeGamesById.get(String(gameId));
			if (!storeGame) {
				return {
					gameID: gameId,
					name: "Unknown game",
					poster: null,
					availableToClient: availabilitySet.has(String(gameId)),
					metadataStatus: "missing",
				};
			}

			return {
				...storeGame,
				gameID: gameId,
				availableToClient: availabilitySet.has(String(gameId)),
				metadataStatus: "found",
			};
		});

	const hasActiveDrag = () => draggedGameIdRef.current !== null;

	const clearDragOperationalRefs = () => {
		draggedGameIdRef.current = null;
		draggedModeRef.current = null;
		dragSnapshotRef.current = null;
		dragPreviewCardsRef.current = null;
		lastPreviewTargetIdRef.current = null;
		acceptedDropRef.current = false;
		dragPreviewChangedRef.current = false;
	};

	const discardDragPreview = (updateVisualState = true) => {
		clearDragOperationalRefs();
		if (updateVisualState) setDraggedGameId(null);
	};

	const cancelDragPreview = () => {
		if (!hasActiveDrag()) return false;
		const snapshot = dragSnapshotRef.current;
		if (Array.isArray(snapshot)) setCurrentCards([...snapshot]);
		discardDragPreview();
		return true;
	};

	const commitDragPreview = () => {
		if (!hasActiveDrag()) return false;
		const changed = dragPreviewChangedRef.current;
		const movedGameId = draggedGameIdRef.current;
		const previewCards = dragPreviewCardsRef.current;
		if (changed && Array.isArray(previewCards)) {
			setMovedGameIds((current) => {
				const candidates = new Set([...current, String(movedGameId)]);
				return [...candidates].filter((gameId) => {
					const initialIndex = initialOrderedIds.findIndex((id) => gameIdsEqual(id, gameId));
					const currentIndex = previewCards.findIndex((game) => gameIdsEqual(game.gameID, gameId));
					return initialIndex >= 0 && currentIndex >= 0 && initialIndex !== currentIndex;
				});
			});
		}
		discardDragPreview();
		if (changed) setSaveError("");
		return true;
	};

	useEffect(() => {
		Interface.request("get", { class: "data", module: "gamesSection", method: "list" }, {})
			.then((list) => {
				if (!mountedRef.current) return;
				setSections(list.filter(isGameSectionActive));
				setSectionsLoading(false);
			})
			.catch((error) => {
				if (!mountedRef.current) return;
				const message = getErrorText(error, "Couldn't load sections");
				setSectionsError(message);
				setSectionsLoading(false);
				if (!isInterfaceApiError(error)) Interface.addNoty("Error", message);
			});

		return () => {
			mountedRef.current = false;
			discardDragPreview(false);
		};
	}, [Interface]);

	const selectSection = (section) => {
		if (saving) return;
		const orderedGameIds = section.game_ids;
		const committedAssignments = cloneAssignmentsByGameId(badgeBootstrap.persistedAssignmentsByGameId);
		setSelectedSection(section);
		setToolView(null);
		setJsonTab("export");
		setJsonDraft("");
		setGeneratedJson(null);
		setExportError("");
		setJsonError("");
		setJsonImportPending(false);
		setBeforeImportIds(null);
		setInactiveDraft(null);
		setActiveVariant("before");
		setReviewDetailsOpen(false);
		setReviewDetailsTab("Added");
		setSelectedCardIds([]);
		setBulkIds("");
		setBulkResult("");
		setBulkBadge(null);
		setBulkPosition(null);
		setBulkRtp("");
		setBulkBadgeError("");
		setActiveMode("available");
		discardDragPreview();
		setSaveError("");
		setInitialOrderedIds([...orderedGameIds]);
		setCurrentCards(buildSectionCards(orderedGameIds));
		setMovedGameIds([]);
		setBadgeDefinitions(badgeBootstrap.definitions.map((definition) => ({ ...definition })));
		setBaselineAssignmentsByGameId(committedAssignments);
		setDraftAssignmentsByGameId(cloneAssignmentsByGameId(committedAssignments));
		setAddSelectionTab("selected");
		setIdsCopyError("");
	};

	const currentOrderedIds = useMemo(() => currentCards.map((game) => game.gameID), [currentCards]);
	const sectionDirty = useMemo(
		() => currentOrderedIds.length !== initialOrderedIds.length || currentOrderedIds.some((gameId, index) => !gameIdsEqual(gameId, initialOrderedIds[index])),
		[currentOrderedIds, initialOrderedIds],
	);
	const dirtyPositionsByGameId = useMemo(
		() => getDirtyPositionsByGameId(baselineAssignmentsByGameId, draftAssignmentsByGameId, badgeDefinitions),
		[badgeDefinitions, baselineAssignmentsByGameId, draftAssignmentsByGameId],
	);
	const dirtyBadgeGameIds = useMemo(() => Object.keys(dirtyPositionsByGameId), [dirtyPositionsByGameId]);
	const badgeDirty = badgeBootstrap.status === "ready" && dirtyBadgeGameIds.length > 0;
	const dirty = sectionDirty || badgeDirty;
	useEffect(() => {
		if (!sectionDirty) setJsonImportPending(false);
	}, [sectionDirty]);
	const reviewChanges = useMemo(() => {
		const baselinePositions = new Map((beforeImportIds || initialOrderedIds).map((id, index) => [String(id), index + 1]));
		const currentPositions = new Map(currentOrderedIds.map((id, index) => [String(id), index + 1]));
		const describe = (id) => ({ id, name: storeGamesById.get(String(id))?.name || "Unknown game", before: baselinePositions.get(String(id)), after: currentPositions.get(String(id)) });
		return [
			{ label: "Added", games: currentOrderedIds.filter((id) => !baselinePositions.has(String(id))).map(describe) },
			{ label: "Removed", games: (beforeImportIds || initialOrderedIds).filter((id) => !currentPositions.has(String(id))).map(describe) },
			{ label: "Position changed", games: currentOrderedIds.filter((id) => baselinePositions.has(String(id)) && baselinePositions.get(String(id)) !== currentPositions.get(String(id))).map(describe) },
		];
	}, [beforeImportIds, initialOrderedIds, currentOrderedIds, storeGamesById]);
	const hasInvalidRtpDraft = useMemo(
		() =>
			dirtyBadgeGameIds.some((gameId) =>
				dirtyPositionsByGameId[gameId].some((position) => {
					const assignment = getAssignmentAtPosition(draftAssignmentsByGameId, gameId, position);
					return assignment && isRtpAssignment(assignment, badgeDefinitions) && !isValidRtpValue(assignment.rtpValue);
				}),
			),
		[badgeDefinitions, dirtyBadgeGameIds, dirtyPositionsByGameId, draftAssignmentsByGameId],
	);
	const dragActive = draggedGameId !== null;
	const availableCards = useMemo(() => currentCards.filter((game) => game.availableToClient === true), [currentCards]);
	const availableCount = availableCards.length;
	const unknownCount = currentCards.filter((game) => game.metadataStatus === "missing").length;
	const unavailableCount = currentCards.filter((game) => game.metadataStatus === "found" && !game.availableToClient).length;
	const totalCount = currentCards.length;
	const fullPositionByGameId = useMemo(
		() => new Map(currentCards.map((game, index) => [String(game.gameID), index + 1])),
		[currentCards],
	);
	const currentIdSet = useMemo(() => new Set(currentOrderedIds.map(String)), [currentOrderedIds]);
	const pickerOptions = useMemo(
		() =>
			addGameCatalog.map((game) => {
				const alreadyInSection = currentIdSet.has(String(game.gameID));
				return {
					label: game.name,
					value: String(game.gameID),
					description: `GameID: ${game.gameID}${alreadyInSection ? " · already in section" : ""}`,
					disabled: alreadyInSection,
				};
			}),
		[addGameCatalog, currentIdSet],
	);

	const idAnalysis = useMemo(
		() => parseGameIdList(bulkIds, storeGamesById, availabilitySet, currentIdSet),
		[bulkIds, storeGamesById, availabilitySet, currentIdSet],
	);
	const idTokens = idAnalysis.tokens;
	const invalidIdCount = idTokens.filter((token) => ["invalid", "unknown", "unavailable"].includes(token.status)).length;
	const bulkError = invalidIdCount ? `${invalidIdCount} invalid Game ${invalidIdCount === 1 ? "ID" : "IDs"}. Fix or remove the red entries.` : "";
	const selectedGameOptions = useMemo(() => {
		const optionsById = new Map(pickerOptions.map((option) => [option.value, option]));
		return idTokens.filter((token) => token.status === "valid").map((token) => optionsById.get(token.gameId));
	}, [idTokens, pickerOptions]);
	const skippedIds = [...new Set(idTokens.filter((token) => token.status === "duplicate" || token.status === "already-present").map((token) => token.gameId))];
	const selectionNotice = skippedIds.length ? `Skipped already present or repeated IDs: ${skippedIds.join(", ")}.` : "";

	const updateGameSelection = (options) => {
		if (saving || hasActiveDrag() || bulkError) return;
		setBulkIds(options.map((option) => option.value).join("\n"));
		setIdsCopyError("");
	};

	const editSelectedIds = (value) => {
		if (saving || hasActiveDrag()) return;
		setBulkIds(value);
		setIdsCopyError("");
	};

	const copySelectedIds = async () => {
		if (saving || hasActiveDrag() || bulkError || !selectedGameOptions.length) return;
		setSaving(true);
		setIdsCopyError("");
		try {
			if (!navigator.clipboard?.writeText) throw new Error("Clipboard is unavailable in this browser.");
			await navigator.clipboard.writeText(selectedGameOptions.map((option) => option.value).join("\n"));
			if (mountedRef.current) Interface.addNoty("Success", "Game IDs copied");
		} catch (error) {
			if (mountedRef.current) setIdsCopyError(`Couldn't copy Game IDs. ${error.message} You can select and copy the text manually.`);
		} finally {
			if (mountedRef.current) setSaving(false);
		}
	};

	const addSelectedGames = () => {
		if (saving || hasActiveDrag() || bulkError || !selectedGameOptions.length) return;
		const result = idAnalysis;
		setCurrentCards([...currentCards, ...buildSectionCards(result.gameIds.map(Number))]);
		setMovedGameIds((ids) => result.gameIds.reduce((movedIds, id, index) => {
			const baselineIndex = initialOrderedIds.findIndex((initialId) => gameIdsEqual(initialId, id));
			return updateMovedGameIdsForAddedCard(movedIds, id, baselineIndex, currentCards.length + index);
		}, ids));
		setSelectedCardIds([]);
		setAddSelectionTab("selected");
		setIdsCopyError("");
		setBulkIds("");
		setSaveError("");
		setToolView(null);
		setBulkResult(`Added ${result.gameIds.length} games to the end.${result.skippedIds.length ? ` Skipped already present or repeated IDs: ${result.skippedIds.join(", ")}.` : ""}`);
	};

	const removeSelectedGames = () => {
		if (saving || hasActiveDrag() || !selectedCardIds.length) return;
		const selectedIds = new Set(selectedCardIds);
		setCurrentCards((cards) => cards.filter((game) => !selectedIds.has(String(game.gameID))));
		setMovedGameIds((ids) => ids.filter((id) => !selectedIds.has(String(id))));
		setSelectedCardIds([]);
		setSaveError("");
	};

	const removeGame = (gameId) => {
		if (saving || hasActiveDrag()) return;
		setSelectedCardIds((ids) => ids.filter((id) => !gameIdsEqual(id, gameId)));
		setCurrentCards((cards) => cards.filter((game) => !gameIdsEqual(game.gameID, gameId)));
		setMovedGameIds((gameIds) => gameIds.filter((id) => !gameIdsEqual(id, gameId)));
		setSaveError("");
	};

	const restoreGamePosition = (gameId) => {
		if (saving || hasActiveDrag()) return;
		const nextCards = restoreCardToInitialIndex(currentCards, initialOrderedIds, gameId);
		if (nextCards === currentCards) return;
		setCurrentCards(nextCards);
		setMovedGameIds((gameIds) =>
			gameIds.filter((id) => {
				if (gameIdsEqual(id, gameId)) return false;
				const baselineIndex = initialOrderedIds.findIndex((initialId) => gameIdsEqual(initialId, id));
				const nextIndex = nextCards.findIndex((game) => gameIdsEqual(game.gameID, id));
				return baselineIndex >= 0 && nextIndex >= 0 && baselineIndex !== nextIndex;
			}),
		);
		setSaveError("");
	};

	const stopControlPropagation = (event) => {
		if (event?.stopPropagation) event.stopPropagation();
	};

	const stopControlDrag = (event) => {
		stopControlPropagation(event);
		if (event?.preventDefault) event.preventDefault();
	};

	const setDraftAssignment = (gameId, position, nextAssignment) => {
		if (saving || hasActiveDrag() || badgeBootstrap.status !== "ready") return;
		setDraftAssignmentsByGameId((current) =>
			replaceAssignmentAtPosition(current, gameId, position, nextAssignment),
		);
		setSaveError("");
	};

	const removeBadge = (gameId, position) => {
		setDraftAssignment(gameId, position, null);
	};

	const restoreRemovedBadge = (gameId, position) => {
		const baselineAssignment = getAssignmentAtPosition(
			baselineAssignmentsByGameId,
			gameId,
			position,
		);
		if (!baselineAssignment) return;
		setDraftAssignment(gameId, position, { ...baselineAssignment });
	};

	const assignBadge = (gameId, position, itemId) => {
		if (itemId === "rtpUp") {
			const activeRtpDefinition = badgeDefinitions.find(
				(definition) => definition.kind === "rtpUp" && definition.status === "active",
			);
			setDraftAssignment(gameId, position, {
				badgeId: activeRtpDefinition?.id || null,
				systemType: "rtpUp",
				position,
				rtpValue: "",
			});
			return;
		}

		const badgeId = itemId.replace("custom:", "");
		const definition = getDefinition(badgeDefinitions, badgeId);
		if (!definition || definition.kind !== "custom" || definition.status !== "active") return;
		setDraftAssignment(gameId, position, { badgeId: definition.id, position });
	};

	const assignSelectedBadge = () => {
		if (saving || hasActiveDrag() || badgeBootstrap.status !== "ready" || !selectedCardIds.length || !bulkBadge || !bulkPosition) return;
		if (selectedCardIds.some((id) => !storeGamesById.has(id))) {
			setBulkBadgeError("Cannot assign badges to unknown games. Deselect unknown games first.");
			return;
		}
		if (bulkBadge.value === "rtpUp" && !isValidRtpValue(bulkRtp)) {
			setBulkBadgeError("RTP Up requires a positive whole number.");
			return;
		}
		const definition = bulkBadge.value === "rtpUp"
			? badgeDefinitions.find((item) => item.kind === "rtpUp" && item.status === "active")
			: getDefinition(badgeDefinitions, bulkBadge.value);
		const assignment = bulkBadge.value === "rtpUp"
			? { badgeId: definition?.id || null, systemType: "rtpUp", position: bulkPosition.value, rtpValue: Number(bulkRtp) }
			: { badgeId: definition.id, position: bulkPosition.value };
		setDraftAssignmentsByGameId((current) => applyBadgeToGames(current, selectedCardIds, assignment,
			(left, right) => getBadgeIdentity(left, badgeDefinitions) === getBadgeIdentity(right, badgeDefinitions)));
		setBulkBadgeError("");
		setSaveError("");
	};

	const clearSelectedBadges = () => {
		if (saving || hasActiveDrag() || badgeBootstrap.status !== "ready" || !selectedCardIds.length) return;
		setDraftAssignmentsByGameId((current) => clearBadgesForGames(current, selectedCardIds));
		setBulkBadgeError("");
		setSaveError("");
	};

	const bulkBadgeOptions = [
		...badgeDefinitions.filter((definition) => definition.kind === "custom" && definition.status === "active")
			.map((definition) => ({ label: definition.name, value: String(definition.id) })),
		...(badgeDefinitions.some((definition) => definition.kind === "rtpUp" && definition.status === "active") || !badgeDefinitions.some((definition) => definition.kind === "rtpUp")
			? [{ label: "RTP Up", value: "rtpUp" }] : []),
	];

	const updateRtpValue = (gameId, position, value) => {
		const currentAssignment = getAssignmentAtPosition(draftAssignmentsByGameId, gameId, position);
		if (!currentAssignment || !isRtpAssignment(currentAssignment, badgeDefinitions)) return;
		setDraftAssignment(gameId, position, { ...currentAssignment, rtpValue: value });
	};

	const getBadgePickerItems = (gameId, position, undoOnly = false) => {
		const assignments = draftAssignmentsByGameId[String(gameId)] || [];
		const usedIdentities = new Set(
			assignments
				.filter((assignment) => assignment.position !== position)
				.map((assignment) => getBadgeIdentity(assignment, badgeDefinitions)),
		);
		const baselineAssignment = getAssignmentAtPosition(
			baselineAssignmentsByGameId,
			gameId,
			position,
		);
		const removedIdentity = baselineAssignment
			? getBadgeIdentity(baselineAssignment, badgeDefinitions)
			: null;
		const removedDefinition = baselineAssignment
			? getDefinition(badgeDefinitions, baselineAssignment.badgeId)
			: null;
		const removedLabel = baselineAssignment
			? (
				isRtpAssignment(baselineAssignment, badgeDefinitions)
					? `RTP Up${isValidRtpValue(baselineAssignment.rtpValue) ? ` (${Number(baselineAssignment.rtpValue)}%)` : ""}`
					: removedDefinition?.name || `Badge #${baselineAssignment.badgeId}`
			)
			: "";
		const undoItem = baselineAssignment
			? {
				text: `Undo removal — ${removedLabel}${removedDefinition?.status === "inactive" ? " (Inactive)" : ""}`,
				id: "undoRemoval",
				disabled: usedIdentities.has(removedIdentity),
				disabledReason: usedIdentities.has(removedIdentity)
					? "Already assigned to another position"
					: undefined,
			}
			: null;
		if (undoOnly) return undoItem ? [undoItem] : [];
		const customItems = badgeDefinitions
			.filter(
				(definition) =>
					definition.kind === "custom" &&
					definition.status === "active" &&
					`custom:${String(definition.id)}` !== removedIdentity,
			)
			.map((definition) => ({
				text: definition.name,
				id: `custom:${definition.id}`,
				disabled: usedIdentities.has(`custom:${String(definition.id)}`),
			}));
		const rtpDefinitions = badgeDefinitions.filter((definition) => definition.kind === "rtpUp");
		const activeRtpDefinition = rtpDefinitions.find((definition) => definition.status === "active");
		if ((activeRtpDefinition || !rtpDefinitions.length) && removedIdentity !== "rtpUp") {
			customItems.push({
				text: "RTP Up",
				id: "rtpUp",
				disabled: usedIdentities.has("rtpUp"),
			});
		}
		if (undoItem) customItems.unshift(undoItem);
		return customItems;
	};

	const moveGame = (fromGameId, toGameId, dragMode) => {
		if (
			saving ||
			!hasActiveDrag() ||
			fromGameId === null ||
			fromGameId === undefined ||
			toGameId === null ||
			toGameId === undefined ||
			gameIdsEqual(fromGameId, toGameId) ||
			dragMode !== activeMode ||
			dragMode !== draggedModeRef.current
		) return;

		setCurrentCards((cards) => {
			if (!hasActiveDrag() || dragMode !== draggedModeRef.current) return cards;
			let next;
			if (dragMode === "available") {
				const reorderedAvailableCards = cards.filter((game) => game.availableToClient === true);
				const fromIndex = reorderedAvailableCards.findIndex((game) => gameIdsEqual(game.gameID, fromGameId));
				const toIndex = reorderedAvailableCards.findIndex((game) => gameIdsEqual(game.gameID, toGameId));
				if (fromIndex < 0 || toIndex < 0) return cards;

				const [moved] = reorderedAvailableCards.splice(fromIndex, 1);
				reorderedAvailableCards.splice(toIndex, 0, moved);
				let availableIndex = 0;
				next = cards.map((game) => game.availableToClient === true ? reorderedAvailableCards[availableIndex++] : game);
			} else if (dragMode === "all") {
				next = [...cards];
				const fromIndex = next.findIndex((game) => gameIdsEqual(game.gameID, fromGameId));
				const toIndex = next.findIndex((game) => gameIdsEqual(game.gameID, toGameId));
				if (fromIndex < 0 || toIndex < 0) return cards;
				const [moved] = next.splice(fromIndex, 1);
				next.splice(toIndex, 0, moved);
			} else {
				return cards;
			}

			const snapshot = dragSnapshotRef.current;
			dragPreviewChangedRef.current = !Array.isArray(snapshot) || !gameCardOrdersEqual(next, snapshot);
			dragPreviewCardsRef.current = next;
			return next;
		});
	};

	const handleDragStart = (event, game) => {
		const unavailableOutsideAll = activeMode !== "all" && game.availableToClient !== true;
		if (
			saving ||
			hasActiveDrag() ||
			unavailableOutsideAll
		) {
			event.preventDefault();
			return;
		}

		draggedGameIdRef.current = game.gameID;
		draggedModeRef.current = activeMode;
		dragSnapshotRef.current = [...currentCards];
		dragPreviewCardsRef.current = [...currentCards];
		lastPreviewTargetIdRef.current = null;
		acceptedDropRef.current = false;
		dragPreviewChangedRef.current = false;
		setDraggedGameId(game.gameID);
		event.dataTransfer.effectAllowed = "move";
		event.dataTransfer.setData("text/plain", String(game.gameID));
	};

	const handleDragEnter = (targetGameId) => {
		if (!hasActiveDrag()) return;
		const draggedGameIdValue = draggedGameIdRef.current;
		const dragMode = draggedModeRef.current;
		if (
			targetGameId === null ||
			targetGameId === undefined ||
			dragMode !== activeMode ||
			(dragMode !== "available" && dragMode !== "all")
		) return;

		const projection = dragMode === "available" ? availableCards : currentCards;
		if (!projection.some((game) => gameIdsEqual(game.gameID, targetGameId))) return;

		if (gameIdsEqual(draggedGameIdValue, targetGameId)) {
			lastPreviewTargetIdRef.current = draggedGameIdValue;
			return;
		}
		if (
			lastPreviewTargetIdRef.current !== null &&
			gameIdsEqual(lastPreviewTargetIdRef.current, targetGameId)
		) return;

		lastPreviewTargetIdRef.current = targetGameId;
		moveGame(draggedGameIdValue, targetGameId, dragMode);
	};

	const handleGridDragOver = (event) => {
		if (hasActiveDrag() && draggedModeRef.current === activeMode) event.preventDefault();
	};

	const handleGridDrop = (event) => {
		if (!hasActiveDrag() || draggedModeRef.current !== activeMode) return;
		event.preventDefault();
		acceptedDropRef.current = true;
	};

	const handleDragEnd = () => {
		if (!hasActiveDrag()) return;
		if (acceptedDropRef.current && draggedModeRef.current === activeMode) {
			commitDragPreview();
			return;
		}
		cancelDragPreview();
	};

	const captureVariantDraft = () => ({
		cards: [...currentCards],
		movedIds: [...movedGameIds],
		assignments: cloneAssignmentsByGameId(draftAssignmentsByGameId),
		importPending: jsonImportPending,
		addIds: bulkIds,
		addTab: addSelectionTab,
		mode: activeMode,
	});

	const resetVariantTools = () => {
		setToolView(null);
		setSelectedCardIds([]);
		setBulkBadgeOpen(false);
		setBulkBadge(null);
		setBulkPosition(null);
		setBulkRtp("");
		setBulkBadgeError("");
		setBulkResult("");
		setIdsCopyError("");
		setSaveError("");
		setJsonError("");
		setExportError("");
		setGeneratedJson(null);
		setReviewDetailsOpen(false);
		setReviewDetailsTab("Added");
	};

	const switchVariant = ({ detail }) => {
		if (saving || hasActiveDrag() || detail.activeTabId === activeVariant) return;
		const nextDraft = inactiveDraft;
		setInactiveDraft(captureVariantDraft());
		setCurrentCards([...nextDraft.cards]);
		setMovedGameIds([...nextDraft.movedIds]);
		setDraftAssignmentsByGameId(cloneAssignmentsByGameId(nextDraft.assignments));
		setJsonImportPending(nextDraft.importPending);
		setBulkIds(nextDraft.addIds);
		setAddSelectionTab(nextDraft.addTab);
		setActiveMode(nextDraft.mode);
		setActiveVariant(detail.activeTabId);
		resetVariantTools();
	};

	const importSectionJson = () => {
		if (saving || hasActiveDrag() || !canImportSection) return;
		try {
			const imported = parseSectionJson(jsonDraft);
			const importedDraft = importReviewActive && activeVariant === "before" ? inactiveDraft : captureVariantDraft();
			if (!importReviewActive) setBeforeImportIds([...currentOrderedIds]);
			if (!importReviewActive || activeVariant === "before") setInactiveDraft(captureVariantDraft());
			setDraftAssignmentsByGameId(cloneAssignmentsByGameId(importedDraft.assignments));
			setBulkIds(importedDraft.addIds);
			setAddSelectionTab(importedDraft.addTab);
			setActiveVariant("imported");
			resetVariantTools();
			setReviewDetailsOpen(false);
			setReviewDetailsTab("Added");
			setJsonImportPending(imported.game_ids.length !== initialOrderedIds.length || imported.game_ids.some((id, index) => !gameIdsEqual(id, initialOrderedIds[index])));
			setToolView(null);
			const importedCards = buildSectionCards(imported.game_ids);
			setCurrentCards(importedCards);
			setActiveMode("all");
			setBulkResult("");
			setMovedGameIds(imported.game_ids.filter((id, index) => initialOrderedIds.some((initialId) => gameIdsEqual(initialId, id)) && !gameIdsEqual(initialOrderedIds[index], id)).map(String));
			setSelectedCardIds([]);
			setJsonError("");
			setSaveError("");
		} catch (error) {
			setJsonError(`Nothing imported. ${error.message}`);
		}
	};

	const exportSource = useMemo(() => selectedSection ? JSON.stringify({
		sectionId: selectedSection.id,
		game_ids: currentOrderedIds.map(Number),
	}) : "", [selectedSection, currentOrderedIds]);
	const exportText = generatedJson?.source === exportSource ? generatedJson.text : "";

	const generateSectionJson = () => {
		if (saving || hasActiveDrag()) return;
		setExportError("");
		setGeneratedJson({ source: exportSource, text: JSON.stringify({ version: 1, game_ids: currentOrderedIds.map(Number) }, null, 2) });
	};

	const copySectionJson = async () => {
		if (saving || !exportText) return;
		setSaving(true);
		setExportError("");
		try {
			if (!navigator.clipboard?.writeText) throw new Error("Clipboard is unavailable in this browser.");
			await navigator.clipboard.writeText(exportText);
			if (mountedRef.current) Interface.addNoty("Success", "Section JSON copied");
		} catch (error) {
			if (mountedRef.current) setExportError(`Couldn't copy JSON. ${error.message} Select and copy the JSON text below manually.`);
		} finally {
			if (mountedRef.current) setSaving(false);
		}
	};

	const save = async () => {
		if (hasActiveDrag()) {
			cancelDragPreview();
			return;
		}
		if (!selectedSection || !dirty || saving || hasInvalidRtpDraft) return;
		setSaving(true);
		setSaveError("");

		const orderedGameIds = currentCards.map((game) => game.gameID);
		const badgeChanges =
			badgeBootstrap.status === "ready"
				? buildBadgeChanges(dirtyBadgeGameIds, draftAssignmentsByGameId, badgeDefinitions)
				: null;

		try {
			if (sectionDirty || badgeDirty) {
				const response = jsonImportPending && sectionDirty
					? await Interface.request("set", { class: "data", module: "gamesSection", method: "saveGamesOrder" }, {
						sectionId: selectedSection.id,
						expectedOrderedGameIds: initialOrderedIds,
						orderedGameIds,
						importMode: "sectionJson",
						...(badgeBootstrap.status === "ready" ? { badgeChanges } : {}),
					})
					: badgeBootstrap.status === "ready"
						? await Interface.saveGamesOrder(selectedSection.id, initialOrderedIds, orderedGameIds, badgeChanges)
						: await Interface.saveGamesOrder(selectedSection.id, initialOrderedIds, orderedGameIds);
				if (!mountedRef.current) return;
				const committedOrderedGameIds = response.orderedGameIds;
				const savedSectionId = response.sectionId;
				const nextDefinitions = mergeDefinitions(badgeDefinitions, response.createdBadgeDefinitions);
				const nextBaselineAssignments = replaceCommittedAssignments(
					baselineAssignmentsByGameId,
					response.badgeChanges,
				);

				setSelectedCardIds([]);
				setInitialOrderedIds([...committedOrderedGameIds]);
				setCurrentCards(buildSectionCards(committedOrderedGameIds));
				setMovedGameIds([]);
				setBadgeDefinitions(nextDefinitions);
				setBaselineAssignmentsByGameId(nextBaselineAssignments);
				setDraftAssignmentsByGameId(cloneAssignmentsByGameId(nextBaselineAssignments));
				setSections((currentSections) =>
					currentSections.map((section) =>
						gameIdsEqual(section.id, savedSectionId)
							? { ...section, game_ids: [...committedOrderedGameIds] }
							: section,
					),
				);
				setSelectedSection((currentSection) =>
					currentSection && gameIdsEqual(currentSection.id, savedSectionId)
						? { ...currentSection, game_ids: [...committedOrderedGameIds] }
						: currentSection,
				);
				if (badgeBootstrap.status === "ready") onBadgesCommitted(response);
				setJsonImportPending(false);
				setBeforeImportIds(null);
				setInactiveDraft(null);
				setActiveVariant("before");
				setReviewDetailsOpen(false);
				setReviewDetailsTab("Added");
			}
			Interface.addNoty("Success", `Section "${selectedSection.name}" saved`);
			setSaving(false);
		} catch (error) {
			if (!mountedRef.current) return;
			const message = isGamesOrderConflict(error)
				? "This section was changed by another user. Close and reopen Manage before saving again."
				: getErrorText(error, "Couldn't save games order");
			setSaveError(message);
			if (!isInterfaceApiError(error)) Interface.addNoty("Error", message);
			setSaving(false);
		}
	};

	const goBack = () => {
		if (saving) return;
		setActiveMode("available");
		discardDragPreview();
		setSelectedSection(null);
		setToolView(null);
		setJsonImportPending(false);
		setBeforeImportIds(null);
		setInactiveDraft(null);
		setActiveVariant("before");
		setReviewDetailsOpen(false);
		setReviewDetailsTab("Added");
		setSelectedCardIds([]);
		setSaveError("");
		setInitialOrderedIds([]);
		setCurrentCards([]);
		setMovedGameIds([]);
		setBadgeDefinitions([]);
		setBaselineAssignmentsByGameId({});
		setDraftAssignmentsByGameId({});
		setAddSelectionTab("selected");
		setIdsCopyError("");
	};

	const renderSections = () => {
		if (sectionsLoading) {
			return (
				<Box textAlign="center" padding={{ vertical: "xl" }}>
					<Spinner size="large" />
				</Box>
			);
		}
		if (sectionsError) return <Alert type="error">{sectionsError}</Alert>;
		if (!sections.length) return <Box textAlign="center" color="text-body-secondary">No active sections</Box>;

		return (
			<SpaceBetween direction="vertical" size="xs">
				{sections.map((section) => (
					<div key={section.id} style={{ borderBottom: "1px solid #eaeded", padding: "8px 0" }}>
						<Button variant="link" onClick={() => selectSection(section)}>
							{section.name}
						</Button>
					</div>
				))}
			</SpaceBetween>
		);
	};

	const renderBadgeVisual = (assignment, definition, dirty) => {
		const isRtpBadge = isRtpAssignment(assignment, badgeDefinitions);
		const hasValidRtp = isValidRtpValue(assignment.rtpValue);
		const isBottom = assignment.position === "bottom_left" || assignment.position === "bottom_right";
		const inactive = definition?.status === "inactive";
		const label = isRtpBadge
			? (hasValidRtp ? `${Number(assignment.rtpValue)}%` : "RTP Up")
			: definition?.name || `Badge #${assignment.badgeId}`;

		return (
			<div
				style={{
					position: "absolute",
					zIndex: 2,
					display: "flex",
					alignItems: "center",
					boxSizing: "content-box",
					maxWidth: "80%",
					overflow: "hidden",
					padding: "1% 10%",
					...(isRtpBadge ? { paddingRight: "10px" } : {}),
					fontWeight: 500,
					fontSize: "80%",
					lineHeight: "16px",
					pointerEvents: "none",
					color: isRtpBadge ? "#fff" : definition?.textColor || "#fff",
					background: isRtpBadge ? "#dc3545" : definition?.backgroundColor || "#1070e3",
					border: dirty ? "2px solid #0972d3" : undefined,
					boxShadow: dirty ? "0 0 0 2px #fff, 0 0 0 5px #0972d3" : undefined,
					opacity: inactive ? 0.55 : 1,
					...badgePositionStyles[assignment.position],
				}}
			>
				<span style={{ display: "inline-flex", alignItems: "center", minWidth: 0, transform: isBottom ? "translateY(1px)" : undefined }}>
					<span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						{label}{inactive ? " · Inactive" : ""}
					</span>
					{isRtpBadge ? (
						<span
							aria-hidden="true"
							style={{
								display: "inline-block",
								width: 0,
								height: 0,
								marginLeft: "2px",
								flexShrink: 0,
								borderTop: "3px solid transparent",
								borderBottom: "3px solid transparent",
								borderLeft: "5px solid #fff",
								transform: "rotate(270deg) scale(1.5)",
							}}
						/>
					) : null}
				</span>
			</div>
		);
	};

	const renderBadgeSlots = (game) => {
		const isMissing = game.metadataStatus === "missing";
		const controlsDisabled = saving || dragActive || badgeBootstrap.status !== "ready";

		return badgePositions.map((position) => {
			const assignment = getAssignmentAtPosition(draftAssignmentsByGameId, game.gameID, position);
			const baselineAssignment = getAssignmentAtPosition(baselineAssignmentsByGameId, game.gameID, position);
			const dirty = (dirtyPositionsByGameId[String(game.gameID)] || []).includes(position);
			const pendingDeletion = dirty && baselineAssignment && !assignment;
			const unknownUndoOnly = isMissing && Boolean(pendingDeletion);
			const definition = assignment ? getDefinition(badgeDefinitions, assignment.badgeId) : null;
			const isRtpBadge = assignment && isRtpAssignment(assignment, badgeDefinitions);
			const isRight = position === "top_right" || position === "bottom_right";
			const isBottom = position === "bottom_left" || position === "bottom_right";
			const cornerStyle = {
				...(isRight ? { right: "4px" } : { left: "4px" }),
				...(isBottom ? { bottom: "4px" } : { top: "4px" }),
			};

			if (assignment) {
				return (
					<React.Fragment key={position}>
						{renderBadgeVisual(assignment, definition, dirty)}
						<div
							draggable={false}
							onPointerDown={stopControlPropagation}
							onMouseDown={stopControlPropagation}
							onClick={stopControlPropagation}
							onDragStart={stopControlDrag}
							onDragEnter={stopControlPropagation}
							style={{
								position: "absolute",
								zIndex: 3,
								...(isRight ? { right: "-4px" } : { left: "-4px" }),
								...(isBottom ? { bottom: "-4px" } : { top: "-4px" }),
							}}
						>
							<button
								type="button"
								draggable={false}
								disabled={controlsDisabled}
								aria-label={`Remove badge from ${position}`}
								onClick={(event) => {
									stopControlPropagation(event);
									removeBadge(game.gameID, position);
								}}
								style={{
									width: "20px",
									height: "20px",
									padding: 0,
									border: "1px solid #879596",
									borderRadius: "50%",
									background: "#fff",
									color: "#16191f",
									cursor: controlsDisabled ? "default" : "pointer",
									lineHeight: "16px",
								}}
							>
								×
							</button>
						</div>
						{isRtpBadge ? (
							<div
								draggable={false}
								onPointerDown={stopControlPropagation}
								onMouseDown={stopControlPropagation}
								onClick={stopControlPropagation}
								onDragStart={stopControlDrag}
								onDragEnter={stopControlPropagation}
								style={{
									position: "absolute",
									zIndex: 3,
									width: "76px",
									...(isRight ? { right: "4px" } : { left: "4px" }),
									...(isBottom ? { bottom: "30px" } : { top: "30px" }),
								}}
							>
								<Input
									type="number"
									inputMode="numeric"
									value={String(assignment.rtpValue ?? "")}
									disabled={controlsDisabled || definition?.status === "inactive"}
									invalid={!isValidRtpValue(assignment.rtpValue)}
									ariaLabel={`RTP value at ${position}`}
									onChange={({ detail }) => updateRtpValue(game.gameID, position, detail.value)}
								/>
							</div>
						) : null}
					</React.Fragment>
				);
			}

			if (isMissing && !unknownUndoOnly) return null;
			return (
				<div
					key={position}
					draggable={false}
					onPointerDown={stopControlPropagation}
					onMouseDown={stopControlPropagation}
					onClick={stopControlPropagation}
					onDragStart={stopControlDrag}
					onDragEnter={stopControlPropagation}
					style={{
						position: "absolute",
						zIndex: 3,
						width: "24px",
						height: "24px",
						overflow: "hidden",
						boxSizing: "border-box",
						padding: 0,
						border: pendingDeletion ? "3px solid #0972d3" : "none",
						borderRadius: "4px",
						boxShadow: pendingDeletion ? "0 0 0 2px #fff, 0 0 0 5px #0972d3" : undefined,
						background: "#fff",
						cursor: controlsDisabled ? "default" : "pointer",
						...cornerStyle,
					}}
				>
					<div style={{ position: "absolute", top: "-4px", left: "-4px" }}>
						<ButtonDropdown
							expandToViewport
							variant="icon"
							ariaLabel={unknownUndoOnly ? `Undo badge removal at ${position}` : `Assign badge at ${position}`}
							disabled={controlsDisabled}
							items={getBadgePickerItems(game.gameID, position, unknownUndoOnly)}
							onItemClick={(event) => {
								stopControlPropagation(event);
								if (event.detail.id === "undoRemoval") {
									restoreRemovedBadge(game.gameID, position);
								} else {
									assignBadge(game.gameID, position, event.detail.id);
								}
							}}
						/>
					</div>
					<span
						aria-hidden="true"
						style={{
							position: "absolute",
							inset: "2px",
							zIndex: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							background: "#fff",
							color: controlsDisabled ? "#aab7b8" : "#5f6b7a",
							fontSize: "17px",
							fontWeight: 600,
							lineHeight: 1,
							pointerEvents: "none",
						}}
					>
						{unknownUndoOnly ? "↶" : "+"}
					</span>
				</div>
			);
		});
	};

	const renderGameCard = (game) => {
		const isMissing = game.metadataStatus === "missing";
		const isUnavailable = game.availableToClient !== true;
		const gameName = isMissing ? "Unknown game" : game.name || "Unknown game";
		const poster = game.bigPoster || Poster;
		const fullPosition = fullPositionByGameId.get(String(game.gameID));
		const cardDirtyPositions = dirtyPositionsByGameId[String(game.gameID)] || [];
		const badgeChangeCounts = cardDirtyPositions.reduce(
			(counts, position) => {
				const baseline = getAssignmentAtPosition(baselineAssignmentsByGameId, game.gameID, position);
				const draft = getAssignmentAtPosition(draftAssignmentsByGameId, game.gameID, position);
				if (baseline && !draft) counts.removed += 1;
				else if (!baseline && draft) counts.added += 1;
				else counts.updated += 1;
				return counts;
			},
			{ removed: 0, added: 0, updated: 0 },
		);
		const badgeChangeSummary = [
			badgeChangeCounts.removed ? `Removed ${badgeChangeCounts.removed}` : null,
			badgeChangeCounts.added ? `Added ${badgeChangeCounts.added}` : null,
			badgeChangeCounts.updated ? `Replaced/updated ${badgeChangeCounts.updated}` : null,
		].filter(Boolean).join(" · ");
		const hasBadgeChanges = cardDirtyPositions.length > 0;
		const badgeChangeCount = cardDirtyPositions.length;
		const isOrderMoved = movedGameIds.some((gameId) => gameIdsEqual(gameId, game.gameID));
		const isNewlyAdded = !initialOrderedIds.some((gameId) => gameIdsEqual(gameId, game.gameID));
		const hasOrderCardChange = isOrderMoved || isNewlyAdded;

		return (
			<div
				key={game.gameID}
				draggable={!saving && (activeMode === "all" || !isUnavailable)}
				onDragStart={(event) => handleDragStart(event, game)}
				onDragEnter={() => handleDragEnter(game.gameID)}
				onDragEnd={handleDragEnd}
				style={{
					position: "relative",
					border: draggedGameId !== null && gameIdsEqual(draggedGameId, game.gameID) ? "2px dashed #0972d3" : "1px solid #d5dbdb",
					borderRadius: "8px",
					background: "#fff",
					cursor: "grab",
					overflow: "visible",
					minWidth: 0,
					boxShadow: hasBadgeChanges ? "inset 0 -5px 0 #0972d3, 0 3px 10px rgba(9, 114, 211, 0.35)" : undefined,
					outline: hasOrderCardChange ? "3px dashed #0972d3" : undefined,
					outlineOffset: hasOrderCardChange ? "2px" : undefined,
				}}
			>
				<div style={{ position: "relative", height: "150px" }}>
					<img
						src={poster}
						alt=""
						style={{ position: "relative", zIndex: 0, width: "100%", height: "150px", objectFit: "cover", display: "block", borderRadius: "8px 8px 0 0", opacity: isUnavailable ? 0.45 : 1 }}
					/>
					{isUnavailable ? (
						<div
							style={{
								position: "absolute",
								inset: 0,
								zIndex: 1,
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								gap: "6px",
								background: "rgba(35, 47, 62, 0.45)",
								borderRadius: "8px 8px 0 0",
								color: "#fff",
								fontWeight: 600,
								textAlign: "center",
								pointerEvents: "none",
							}}
						>
							<Icon name="lock-private" variant="inverted" />
							<span>Unavailable to client</span>
						</div>
					) : null}
					{renderBadgeSlots(game)}
				</div>
				<div style={{ padding: "10px 38px 10px 10px" }}>
					<div style={{ float: "left", marginRight: "6px" }} draggable={false} onPointerDown={stopControlPropagation} onMouseDown={stopControlPropagation} onClick={stopControlPropagation} onDragStart={stopControlDrag} onDragEnter={stopControlPropagation}>
						<Checkbox
							ariaLabel={`Select ${gameName}, Game ID ${game.gameID}`}
							checked={selectedCardIds.includes(String(game.gameID))}
							disabled={saving || dragActive}
							onChange={({ detail }) => {
								if (saving || hasActiveDrag()) return;
								setSelectedCardIds((ids) => detail.checked ? [...ids, String(game.gameID)] : ids.filter((id) => id !== String(game.gameID)));
							}}
						/>
					</div>
					<div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={gameName}>
						{gameName}
					</div>
					<div style={{ color: "#5f6b7a", fontSize: "12px", marginTop: "4px" }}>GameID: {game.gameID}</div>
					<div style={{ color: "#687078", fontSize: "11px", marginTop: "4px" }}>Position {fullPosition}</div>
				</div>
				{isOrderMoved ? (
					<div
						draggable={false}
						onPointerDown={stopControlPropagation}
						onMouseDown={stopControlPropagation}
						onClick={stopControlPropagation}
						onDragStart={stopControlDrag}
						onDragEnter={stopControlPropagation}
						style={{ position: "absolute", right: "7px", top: "158px", zIndex: 4 }}
					>
						<button
							type="button"
							draggable={false}
							disabled={saving || dragActive}
							aria-label={`Restore original position for ${gameName}`}
							title="Restore original position"
							onClick={(event) => {
								stopControlPropagation(event);
								restoreGamePosition(game.gameID);
							}}
							style={{
								width: "24px",
								height: "24px",
								padding: 0,
								border: "1px solid #0972d3",
								borderRadius: "50%",
								background: "#fff",
								color: "#0972d3",
								cursor: saving || dragActive ? "default" : "pointer",
								fontSize: "17px",
								fontWeight: 700,
								lineHeight: "20px",
							}}
						>
							↶
						</button>
					</div>
				) : null}
				{hasBadgeChanges ? (
					<div
						style={{
							position: "absolute",
							left: "10px",
							bottom: "-9px",
							zIndex: 4,
							display: "inline-flex",
							alignItems: "center",
							gap: "4px",
							maxWidth: "calc(100% - 58px)",
							height: "18px",
							padding: "0 7px",
							boxSizing: "border-box",
							borderRadius: "9px",
							background: "#0972d3",
							boxShadow: "0 0 0 2px #fff",
							color: "#fff",
							fontSize: "10px",
							fontWeight: 700,
							lineHeight: "18px",
							pointerEvents: "none",
						}}
						title={`Unsaved badge changes: ${badgeChangeSummary}`}
					>
						<span aria-hidden="true">●</span>
						<span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{badgeChangeCount} badge {badgeChangeCount === 1 ? "change" : "changes"}
						</span>
					</div>
				) : null}
				<div
					draggable={false}
					onPointerDown={stopControlPropagation}
					onMouseDown={stopControlPropagation}
					onClick={stopControlPropagation}
					onDragStart={stopControlDrag}
					onDragEnter={stopControlPropagation}
					style={{ position: "absolute", right: "6px", bottom: "8px", zIndex: 4, pointerEvents: dragActive ? "none" : "auto" }}
				>
					<Button
						variant="inline-icon"
						iconName="close"
						ariaLabel={`Remove ${gameName}`}
						disabled={saving || dragActive}
						onClick={(event) => {
							stopControlPropagation(event);
							removeGame(game.gameID);
						}}
					/>
				</div>
			</div>
		);
	};

	const renderGameGrid = (cards) => (
		<>
			<div
				onDragOver={handleGridDragOver}
				onDrop={handleGridDrop}
				style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "14px" }}
			>
				{cards.map(renderGameCard)}
				<button
					type="button"
					draggable={false}
					disabled={saving || dragActive}
					onClick={() => {
						if (saving || hasActiveDrag()) return;
						setToolView("add");
					}}
					style={{
						minHeight: "220px",
						border: "2px dashed #879596",
						borderRadius: "8px",
						background: "#fafafa",
						color: "#0972d3",
						cursor: "pointer",
						fontSize: "44px",
						pointerEvents: dragActive ? "none" : "auto",
					}}
					aria-label="Add games"
				>
					+
				</button>
			</div>

		</>
	);

	const changeMode = ({ detail }) => {
		if (saving) return;
		const nextMode = detail.activeTabId;
		cancelDragPreview();
		setSelectedCardIds([]);
		setActiveMode(nextMode);
	};

	const renderSelectedGames = () => (
		<div role="list" aria-label="Selected games" style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
			{idTokens.map((token, index) => {
				const invalid = ["invalid", "unknown", "unavailable"].includes(token.status);
				const game = token.gameId ? storeGamesById.get(token.gameId) : null;
				const reason = {
					invalid: "Use a positive whole-number ID",
					unknown: "Unknown game",
					unavailable: "Unavailable game",
					duplicate: "Duplicate — skipped",
					"already-present": "Already in section — skipped",
				}[token.status];
				const title = game ? game.name : reason;
				return (
					<div key={token.start} role="listitem" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 32px", alignItems: "center", gap: "8px", minHeight: "56px", minWidth: 0, boxSizing: "border-box", padding: "8px 10px", border: `1px solid ${invalid ? "#d91515" : "#d5dbdb"}`, borderRadius: "8px", background: "#fafafa", color: invalid ? "#d91515" : "#16191f" }}>
						<div style={{ minWidth: 0 }}>
							<div title={title} style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
							<div style={{ fontSize: "12px", marginTop: "4px", overflowWrap: "anywhere" }}>Game ID: {token.raw}{game && reason ? ` · ${reason}` : ""}</div>
						</div>
						<Button
							variant="inline-icon"
							iconName="close"
							ariaLabel={`Remove Game ID ${token.raw}, entry ${index + 1}`}
							disabled={saving || dragActive}
							onClick={() => editSelectedIds(bulkIds.slice(0, token.start) + bulkIds.slice(token.end))}
						/>
					</div>
				);
			})}
		</div>
	);

	const renderTool = () => (
		<SpaceBetween direction="vertical" size="m">
			<Header variant="h2" actions={<Button variant="link" disabled={saving} onClick={() => setToolView(null)}>Back to games</Button>}>
				{toolView === "add" ? "Add games" : "Game order JSON"}
			</Header>
			{toolView === "add" ? <>
				<FormField label="Choose games" description="Search by name or Game ID. Games are added in selection order.">
					<Multiselect
						keepOpen
						hideTokens
						virtualScroll
						filteringType="auto"
						placeholder="Search games"
						selectedOptions={selectedGameOptions}
						options={pickerOptions}
						disabled={saving || dragActive || Boolean(bulkError)}
						onChange={({ detail }) => {
							if (saving || hasActiveDrag() || bulkError) return;
							const nextIds = new Set(detail.selectedOptions.map((option) => option.value));
							const previousIds = new Set(selectedGameOptions.map((option) => option.value));
							updateGameSelection([
								...selectedGameOptions.filter((option) => nextIds.has(option.value)),
								...detail.selectedOptions.filter((option) => !previousIds.has(option.value)),
							]);
						}}
					/>
				</FormField>
				<Box>{selectedGameOptions.length} selected{invalidIdCount ? ` · ${invalidIdCount} invalid` : ""}</Box>

				{selectionNotice ? <Alert type="info">{selectionNotice}</Alert> : null}
				<Tabs
					activeTabId={addSelectionTab}
					onChange={({ detail }) => { if (!saving) setAddSelectionTab(detail.activeTabId); }}
					tabs={[
						{ id: "selected", label: "Selected games", content: (
							<SpaceBetween direction="vertical" size="s">
								{bulkError ? <Box>Fix the red IDs in Game IDs or remove their rows here to continue.</Box> : null}
								{renderSelectedGames()}
							</SpaceBetween>
						) },
						{ id: "ids", label: "Game IDs", content: (
							<SpaceBetween direction="vertical" size="s">
								<FormField label="Game IDs" controlId="section-game-ids" description={<span id="section-game-ids-help">Editing this list replaces your selection. Use spaces, new lines, commas or semicolons.</span>} errorText={bulkError ? <span id="section-game-ids-error">{bulkError}</span> : null}>
									<GamesSectionGameIdsInput
										id="section-game-ids"
										value={bulkIds}
										tokens={idTokens}
										disabled={saving || dragActive}
										hasError={Boolean(bulkError)}
										describedBy={`section-game-ids-help${bulkError ? " section-game-ids-error" : ""}`}
										onChange={editSelectedIds}
									/>
								</FormField>
								<Button disabled={saving || dragActive || Boolean(bulkError) || !selectedGameOptions.length} onClick={copySelectedIds}>Copy IDs</Button>
								{idsCopyError ? <Alert type="error">{idsCopyError}</Alert> : null}
							</SpaceBetween>
						) },
					]}
				/>
				<Button variant="primary" disabled={saving || dragActive || Boolean(bulkError) || !selectedGameOptions.length} onClick={addSelectedGames}>Add {selectedGameOptions.length} {selectedGameOptions.length === 1 ? "game" : "games"}</Button>
			</> : <>
			<Box>Transfers games and their order. Section settings and badges stay unchanged.</Box>
			<Tabs
				activeTabId={canImportSection ? jsonTab : "export"}
				onChange={({ detail }) => setJsonTab(detail.activeTabId)}
				tabs={[
					{ id: "export", label: "Export", content: (jsonTab === "export" || !canImportSection) ? <SpaceBetween direction="vertical" size="s">
						{exportError ? <Alert type="error">{exportError}</Alert> : null}
						<SpaceBetween direction="horizontal" size="xs">
							<Button disabled={saving || dragActive} onClick={generateSectionJson}>Generate JSON</Button>
							<Button disabled={saving || !exportText} onClick={copySectionJson}>Copy JSON</Button>
						</SpaceBetween>
						<Textarea readOnly value={exportText} rows={8} ariaLabel="Exported section JSON" placeholder="Generate JSON to view and copy the current draft." />
					</SpaceBetween> : null },
					...(canImportSection ? [{ id: "import", label: "Import", content: jsonTab === "import" ? <SpaceBetween direction="vertical" size="s">
						{jsonError ? <Alert type="error">{jsonError}</Alert> : null}
						<FormField label="Paste JSON" description="Apply to the draft, then Save from the games screen.">
							<Textarea value={jsonDraft} placeholder="Paste JSON" rows={8} disabled={saving || dragActive} onChange={({ detail }) => { setJsonDraft(detail.value); setJsonError(""); }} />
						</FormField>
						<Button disabled={saving || dragActive || !jsonDraft.trim()} onClick={importSectionJson}>Apply JSON to draft</Button>
					</SpaceBetween> : null }] : []),
				]}
			/>
			</>}
		</SpaceBetween>
	);

	const controlsGridStyle = {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(0, min(100%, 192px)))",
		maxWidth: "592px",
		width: "100%",
		gap: "8px",
		alignItems: "center",
	};

	const renderGames = () => (
		<div style={{ border: `1px solid ${colorBorderDividerDefault}`, borderRadius: borderRadiusContainer }}>
			<style>{`
				.games-section-games-shared-controls {
					display: grid;
					grid-template-columns: minmax(0, max-content) max-content max-content;
					justify-content: start;
					align-items: center;
					gap: 8px;
				}
				@media (max-width: 640px) {
					.games-section-games-shared-controls { grid-template-columns: minmax(0, 1fr); }
				}
			`}</style>
			<div style={{ padding: "12px", background: colorBackgroundContainerHeader, borderBottom: `1px solid ${colorBorderDividerDefault}`, borderRadius: `${borderRadiusContainer} ${borderRadiusContainer} 0 0` }}>
				<div className="games-section-games-shared-controls">
				<div style={{ display: "flex", alignItems: "baseline", gap: "8px", minWidth: 0, maxWidth: "240px" }}>
					<h2 title={selectedSection.name} style={{ margin: 0, maxWidth: "160px", fontSize: "20px", lineHeight: "24px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedSection.name}</h2>
					<span style={{ flexShrink: 0, whiteSpace: "nowrap" }}>{totalCount} games</span>
				</div>
				<div>
				<Button disabled={saving || dragActive} onClick={() => {
					if (saving || hasActiveDrag()) return;
					setSelectedCardIds((activeMode === "available" ? availableCards : currentCards).map((game) => String(game.gameID)));
				}}>Select all</Button>
				</div>
				<div><Button wrapText={false} disabled={saving || dragActive} onClick={() => setToolView("json")}>Import / export games</Button></div>
			{selectedCardIds.length ? <>
				<div style={{ gridColumn: "1 / -1", width: "100%", borderTop: `1px solid ${colorBorderDividerDefault}` }} />
					<Box>{selectedCardIds.length} selected</Box>
					<div><Button variant="link" disabled={saving || dragActive} onClick={() => setSelectedCardIds([])}>Clear</Button></div>
					<div><ButtonDropdown
						disabled={saving || dragActive}
						items={[
							{ id: "badge", text: "Set badge", disabled: badgeBootstrap.status !== "ready" },
							{ id: "clearBadges", text: "Clear badges (all sections)", disabled: badgeBootstrap.status !== "ready" },
							{ id: "remove", text: "Remove games from this section" },
						]}
						onItemClick={({ detail }) => {
							if (saving || hasActiveDrag()) return;
							if (detail.id === "badge") { setBulkBadgeOpen(true); setBulkBadgeError(""); }
							if (detail.id === "clearBadges") clearSelectedBadges();
							if (detail.id === "remove") removeSelectedGames();
						}}
					>Actions</ButtonDropdown></div>

			</> : null}
				</div>
			{selectedCardIds.length > 0 && bulkBadgeOpen ? <div style={{ minWidth: 0, width: "100%", marginTop: "12px", paddingTop: "12px", borderTop: `1px solid ${colorBorderDividerDefault}` }}>
				<div style={{ ...controlsGridStyle, alignItems: "start" }}>
					<div style={{ minWidth: 0 }}><FormField label="Badge" errorText={bulkBadgeError}>
						<Select placeholder="Choose badge" selectedOption={bulkBadge} options={bulkBadgeOptions} disabled={saving || dragActive || badgeBootstrap.status !== "ready"} onChange={({ detail }) => { setBulkBadge(detail.selectedOption); setBulkBadgeError(""); }} />
					</FormField></div>
					<div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "8px" }}><FormField label="Corner">
						<Select placeholder="Choose corner" selectedOption={bulkPosition} options={badgePositions.map((position) => ({ label: position.replace(/_/g, " "), value: position }))} disabled={saving || dragActive || badgeBootstrap.status !== "ready"} onChange={({ detail }) => setBulkPosition(detail.selectedOption)} />
					</FormField>
					{bulkBadge?.value === "rtpUp" ? <div style={{ minWidth: 0 }}><FormField label="RTP Up value"><Input type="number" value={bulkRtp} disabled={saving || dragActive || badgeBootstrap.status !== "ready"} onChange={({ detail }) => setBulkRtp(detail.value)} /></FormField></div> : null}
					</div>
					<FormField label="Actions"><div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
					<Button variant="primary" disabled={saving || dragActive || badgeBootstrap.status !== "ready" || !bulkBadge || !bulkPosition} onClick={assignSelectedBadge}>Apply</Button>
					<Button disabled={saving || dragActive} onClick={() => { setBulkBadgeOpen(false); setBulkBadgeError(""); }}>Cancel</Button>
					</div></FormField>
				</div>
				<Box color="text-body-secondary" margin={{ top: "xs" }}>Badge changes affect all sections on Save.</Box>
			</div> : null}
			</div>
			<div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "12px", minWidth: 0 }}>
				{importReviewActive ? <Tabs disableContentPaddings activeTabId={activeVariant} onChange={switchVariant} tabs={[
					{ id: "before", label: "Before import", disabled: saving || dragActive },
					{ id: "imported", label: "Imported version", disabled: saving || dragActive },
				]} /> : null}
			{reviewVisible ? <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
				<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }}>
					{reviewChanges.map(({ label, games }) => <span key={label}>{label} {games.length}</span>)}
					<Button variant="link" ariaExpanded={reviewDetailsOpen} onClick={() => setReviewDetailsOpen((open) => !open)}>Details</Button>
					{unavailableCount || unknownCount ? <Box color="text-body-secondary">{[unavailableCount ? `${unavailableCount} unavailable` : null, unknownCount ? `${unknownCount} unknown` : null].filter(Boolean).join(" · ")}</Box> : null}
				</div>
				<Box color="text-body-secondary">Compared with before import · Save this version to apply.</Box>
				{reviewDetailsOpen ? <Tabs activeTabId={reviewDetailsTab} onChange={({ detail }) => setReviewDetailsTab(detail.activeTabId)} tabs={reviewChanges.map(({ label, games }) => ({
					id: label,
					label,
					content: reviewDetailsTab === label ? <div style={{ maxHeight: "200px", overflow: "auto" }}>
						<table style={{ width: "100%", tableLayout: "fixed", fontSize: "12px", borderCollapse: "collapse" }}>
							<thead><tr><th style={{ textAlign: "left" }}>Game</th><th style={{ width: "90px", textAlign: "left" }}>ID</th><th style={{ width: "100px", textAlign: "left" }}>{label === "Added" ? "New position" : label === "Removed" ? "Old position" : "Old → new"}</th></tr></thead>
							<tbody>{games.map((game) => <tr key={game.id}>
								<td title={game.name} style={{ padding: "4px 8px 4px 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{game.name}</td>
								<td>{game.id}</td><td>{label === "Added" ? game.after : label === "Removed" ? game.before : `${game.before} → ${game.after}`}</td>
							</tr>)}</tbody>
						</table>
					</div> : null,
				}))} /> : null}
			</div> : null}


			{bulkResult ? <Alert type="info">{bulkResult}</Alert> : null}
			{saveError ? <Alert type="error">{saveError}</Alert> : null}
			{badgeBootstrap.status === "error" ? (
				<Alert type="warning">
					{badgeBootstrap.error || "Couldn't load badges"} Badge editing is disabled; game order can still be saved.
				</Alert>
			) : null}
			<div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" }}>
			{importReviewActive && !reviewVisible && (unavailableCount || unknownCount) ? <Box color="text-body-secondary">{[unavailableCount ? `${unavailableCount} unavailable` : null, unknownCount ? `${unknownCount} unknown` : null].filter(Boolean).join(" · ")}</Box> : null}
			{reviewVisible ? renderGameGrid(currentCards) : (
			<Tabs
				activeTabId={activeMode}
				onChange={changeMode}
				tabs={[
					{
						id: "available",
						label: `Available (${availableCount})`,
						content: activeMode === "available" ? renderGameGrid(availableCards) : null,
					},
					{
						id: "all",
						label: `All (${availableCount}/${totalCount})`,
						content: activeMode === "all" ? renderGameGrid(currentCards) : null,
					},
				]}
			/>
			)}
			</div>
			</div>
		</div>
	);

	return (
		<Modal
			visible
			size={selectedSection ? (toolView ? "large" : "max") : "medium"}
			onDismiss={() => {
				if (!saving) {
					discardDragPreview();
					onClose();
				}
			}}
			header={
				<span style={{ display: "inline-flex", alignItems: "center", gap: "12px", whiteSpace: "nowrap" }}>
					<span>Manage section games</span>
					{selectedSection && !toolView ? <span style={{ flexShrink: 0, whiteSpace: "nowrap" }} title="Back to sections">
						<Button variant="link" iconName="angle-left" wrapText={false} ariaLabel="Back to sections" onClick={goBack} disabled={saving || dragActive}>Return</Button>
					</span> : null}
				</span>
			}
			footer={
				<Box float="right">
					<SpaceBetween direction="horizontal" size="xs">
						<Button
							variant="link"
							onClick={() => {
								if (!saving) {
									discardDragPreview();
									onClose();
								}
							}}
							disabled={saving}
						>
							Cancel
						</Button>
						{selectedSection && !toolView ? (
							<Button
								variant="primary"
								onClick={save}
								loading={saving}
								disabled={!dirty || saving || dragActive || hasInvalidRtpDraft}
							>
								{importReviewActive ? "Save this version" : "Save"}
							</Button>
						) : null}
					</SpaceBetween>
				</Box>
			}
		>
			{selectedSection ? (toolView ? renderTool() : renderGames()) : renderSections()}
		</Modal>
	);
}

export default GamesSectionGamesEditor;
